import {
  IExecutionPlane,
  RuntimeEvent,
  EXECUTION_PLANES,
  NODE_TYPES,
} from "@retreat/core";
import type { BellPatternId, BellInstrumentId, TraditionId } from "@retreat/core";
import { BellLibrary, createAudioContext, BELL_CLEANUP_MS, startSilentUnlock } from "@retreat/bells";
import { TypedEmitter } from "@retreats/engine";
import { createChildLogger } from "@retreat/shared";

// ── Constants ─────────────────────────────────────────────────────────────────

/** Pattern played when no explicit bellPatternId or containerType is provided. */
const FALLBACK_BELL_PATTERN: BellPatternId = "rin_single";

/** DAG node id suffix that identifies closing-bell overlay events. */
const BELL_AFTER_ID_SUFFIX = "bell-after" as const;

/**
 * Vibration pattern emitted alongside each bell strike.
 * Two short pulses separated by a brief pause, mimicking a struck bell.
 * [buzz_ms, pause_ms, buzz_ms]
 */
const BELL_VIBRATION_PATTERN = [40, 25, 40] as const;

// ── Types ─────────────────────────────────────────────────────────────────────

type AudioReadyStatus  = { ready: true };
type AudioFailedStatus = { ready: false; reason: string };
type AudioStatus = AudioReadyStatus | AudioFailedStatus;

interface SampleAudioPlaneEvents {
  "status:change": (event: AudioStatus) => void;
  [key: string]: (...args: any[]) => void;
}

// ── Haptic helper ─────────────────────────────────────────────────────────────

function vibrateBell(): void {
  if (typeof navigator !== "undefined" && navigator.vibrate) {
    navigator.vibrate(BELL_VIBRATION_PATTERN);
  }
}

// ── Class ─────────────────────────────────────────────────────────────────────

/**
 * Manages the AudioContext lifecycle (iOS unlock, suspend/resume) and
 * routes bell events to BellLibrary for instrument-appropriate synthesis.
 *
 * Bell routing priority:
 * 1. event.payload.bellPatternId — explicit pattern from content schema
 * 2. playForContainer(containerType, position) — tradition-appropriate default
 * 3. FALLBACK_BELL_PATTERN — absolute fallback when no routing info is present
 */
export class SampleAudioPlane
  extends TypedEmitter<SampleAudioPlaneEvents>
  implements IExecutionPlane
{
  private log = createChildLogger("SampleAudioPlane");
  private audioContext: AudioContext | null = null;
  private bellLibrary: BellLibrary | null = null;
  private isInitialized = false;
  private preparedBellCount = 0;
  private readonly getBellsEnabled: () => boolean;
  private readonly createCtx: () => AudioContext;

  constructor(options: { getBellsEnabled?: () => boolean; createCtx?: () => AudioContext } = {}) {
    super();
    this.getBellsEnabled = options.getBellsEnabled ?? (() => true);
    this.createCtx = options.createCtx ?? createAudioContext;
  }

  async unlockAudio(): Promise<void> {
    if (this.isInitialized) return;
    try {
      this.audioContext = this.createCtx();
      // iOS ≤14.4: start a zero-duration silent source synchronously while still
      // in the gesture call stack so the AudioContext is permanently unlocked
      // before the first await suspends this function.
      startSilentUnlock(this.audioContext);
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }
      this.bellLibrary = new BellLibrary(this.audioContext);
      this.isInitialized = true;
      this.emit("status:change", { ready: true });
      this.log.info("AudioContext unlocked, BellLibrary ready");
    } catch (err) {
      this.log.error("Failed to unlock audio context", err);
      this.emit("status:change", {
        ready: false,
        reason: (err as Error).message,
      });
      throw err;
    }
  }

  async resume(): Promise<void> {
    return this.unlockAudio();
  }

  async prepare(events: RuntimeEvent[]): Promise<void> {
    this.preparedBellCount = events.filter(
      (e) =>
        e.plane === EXECUTION_PLANES.OVERLAY &&
        e.payload?.type === NODE_TYPES.BELL,
    ).length;
  }

  execute(event: RuntimeEvent): void {
    if (!this.getBellsEnabled()) return;

    if (
      event.plane !== EXECUTION_PLANES.OVERLAY ||
      event.payload?.type !== NODE_TYPES.BELL
    ) {
      return;
    }
    if (!this.audioContext || !this.bellLibrary) {
      this.log.info("AudioContext not initialized, skipping bell");
      return;
    }

    // Let BellLibrary.play() attempt ctx.resume() via ensureRunning() rather
    // than bailing here. On iOS a suspended context can recover if the
    // suspension was brief (tab switch, system alert). Hard-checking
    // state !== "running" here would silently drop the bell in those cases.

    const patternId = event.payload?.bellPatternId as BellPatternId | undefined;
    const containerType: string | undefined = event.payload?.containerType;

    if (patternId) {
      void this.bellLibrary.play(patternId);
    } else if (containerType) {
      const position = (event.id ?? "").includes(BELL_AFTER_ID_SUFFIX) ? "after" : "before";
      void this.bellLibrary.playForContainer(containerType, position);
    } else {
      void this.bellLibrary.play(FALLBACK_BELL_PATTERN);
    }

    vibrateBell();
  }

  sync(_offsetSeconds?: number): void {
    // Do NOT call stopAll() here — that would suspend the AudioContext and
    // silence all bells for the remainder of the retreat. sync() is called on
    // recovery/seek; the AudioContext must stay running.
  }

  stopAll(): void {
    this.log.info("stopAll called");
    if (this.audioContext && this.audioContext.state === "running") {
      this.audioContext.suspend().catch(() => {});
    }
  }

  isReady(): boolean {
    return this.isInitialized && this.audioContext !== null;
  }

  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  getState() {
    return {
      isReady: this.isReady(),
      preparedBellCount: this.preparedBellCount,
    };
  }

  setTradition(traditionId: string): void {
    this.bellLibrary?.setTradition(traditionId as TraditionId);
  }

  clearTradition(): void {
    this.bellLibrary?.clearTradition();
  }

  setAccessibilityPreset(
    preset: "standard" | "gentle" | "tinnitusSafe" | "silent",
  ): void {
    this.bellLibrary?.setAccessibilityPreset(preset);
  }

  playPreviewBell(): void {
    this.playImmediateOrFallback((lib) => lib.playPreview());
  }

  playInstrumentPreview(instrumentId: BellInstrumentId): void {
    this.playImmediateOrFallback((lib) => void lib.previewInstrument(instrumentId));
  }

  playPattern(patternId: BellPatternId): void {
    this.playImmediateOrFallback((lib) => void lib.play(patternId));
  }

  /**
   * Plays via the existing running BellLibrary, or creates a temporary
   * AudioContext with iOS silent-unlock and delegates after it resumes.
   *
   * All three public preview methods share this path so the iOS gesture
   * policy is enforced in one place — startSilentUnlock() runs synchronously
   * within the user-gesture call stack before any await.
   */
  private playImmediateOrFallback(play: (lib: BellLibrary) => void): void {
    if (this.bellLibrary && this.audioContext?.state === "running") {
      play(this.bellLibrary);
      return;
    }
    const tmpCtx = this.createCtx();
    startSilentUnlock(tmpCtx);
    tmpCtx.resume()
      .then(() => import("@retreat/bells"))
      .then(({ BellLibrary: Lib }) => {
        const lib = new Lib(tmpCtx);
        play(lib);
        setTimeout(() => tmpCtx.close(), BELL_CLEANUP_MS);
      })
      .catch(() => {});
  }

  dispose(): void {
    this.stopAll();
    if (this.audioContext) {
      this.audioContext.close().catch(() => {});
    }
    this.bellLibrary = null;
    this.removeAllListeners();
  }
}
